import { useMutation } from '@tanstack/react-query';
import { businessAccessApi } from '../api/businessAccessClient';

/**
 * SAM-1186 — `POST /business_access/receipt` `{ transaction_reference, business_id }` (sam-lambda-functions #862).
 * The server texts the receipt to the phone stored for the guest of the pass or booking: the body picks neither the
 * number nor the text. Answers 200 `{sent:true}`, 429 `rate_limited` (3 per charge per hour), 422 `no_phone`,
 * 404 `payment_not_found` (pending/failed or another business), 403, 502 `sms_error`.
 */
export type ReceiptRequest = { transaction_reference: string; business_id: string };

const MESSAGES: Record<string, string> = {
  rate_limited: 'Too many receipts for this payment. Try again in an hour.',
  no_phone: 'The guest has no phone number on file.',
  payment_not_found: 'This payment has no receipt to send.',
  sms_error: 'The text could not be sent. Try again.',
};

export const receiptErrorMessage = (error: unknown): string => {
  const e = error as { response?: { status?: number; data?: { code?: string; error?: string } } };
  const code = e?.response?.data?.code || e?.response?.data?.error;
  if (code && MESSAGES[code]) return MESSAGES[code];
  if (e?.response?.status === 403) return 'You are not allowed to send receipts for this business.';
  return 'The receipt could not be sent. Try again.';
};

export const useSendPaymentReceipt = () =>
  useMutation({
    mutationFn: (request: ReceiptRequest) =>
      businessAccessApi<{ sent: boolean; code: string | null }>({ url: '/receipt', method: 'POST', data: request, skipGlobalErrorHandling: true }),
  });
